import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { IconButton } from './IconButton.js';
import { iconClose } from './generated/icons.js';
import { SIDE_SHEET_QUERY, cx, useMediaQuery } from './util.js';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Names the dialog for screen readers. */
  label: string;
  className?: string;
  children: ReactNode;
}

/**
 * The platform modal dialog: it traps focus, makes the page inert, closes on Escape and
 * gives focus back to what opened it. Content is only mounted while it is open.
 */
function Modal({ open, onClose, label, className, children }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add('k-scroll-lock');
    }
    if (!open && dialog.open) dialog.close();
    return () => {
      document.documentElement.classList.remove('k-scroll-lock');
    };
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-label={label}
      className={className}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // A tap on the scrim lands on the dialog element itself, not on its content.
        if (event.target === ref.current) onClose();
      }}
    >
      {open ? children : null}
    </dialog>
  );
}

export interface SheetProps extends Omit<ModalProps, 'className'> {
  title: string;
  closeLabel?: string;
  actions?: ReactNode;
}

function PhoneSheet({
  open,
  onClose,
  label,
  title,
  closeLabel = 'Close',
  actions,
  children,
}: SheetProps) {
  const [drag, setDrag] = useState(0);
  const start = useRef<number | null>(null);
  const down = (event: PointerEvent<HTMLDivElement>) => {
    start.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (start.current !== null) setDrag(Math.max(0, event.clientY - start.current));
  };
  const up = () => {
    if (start.current === null) return;
    start.current = null;
    if (drag > 100) onClose();
    setDrag(0);
  };
  return (
    <Modal open={open} onClose={onClose} label={label} className="k-sheet">
      <div
        className="k-sheet__panel"
        style={
          drag > 0 ? { transform: `translateY(${String(drag)}px)`, transition: 'none' } : undefined
        }
      >
        <div
          className="k-sheet__handle"
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          aria-hidden="true"
        >
          <span />
        </div>
        <div className="k-sheet__head">
          <h2 className="k-t-title-lg">{title}</h2>
          <IconButton icon={iconClose} label={closeLabel} onClick={onClose} />
        </div>
        <div className="k-sheet__body">{children}</div>
        {actions === undefined ? null : <div className="k-sheet__actions">{actions}</div>}
      </div>
    </Modal>
  );
}

/**
 * A bottom sheet on a phone, which can be dragged down to close; on a desktop window (1024 px
 * and up, the design's desktop class) the same sheet opens at the side, so a desktop never shows a phone sheet.
 */
export function BottomSheet(props: SheetProps) {
  const side = useMediaQuery(SIDE_SHEET_QUERY);
  return side ? <SideSheet {...props} /> : <PhoneSheet {...props} />;
}

/** The desktop twin of the bottom sheet: it slides in from the end. */
export function SideSheet({
  open,
  onClose,
  label,
  title,
  closeLabel = 'Close',
  actions,
  children,
}: SheetProps) {
  return (
    <Modal open={open} onClose={onClose} label={label} className="k-sidesheet">
      <div className="k-sidesheet__panel">
        <div className="k-sheet__head">
          <h2 className="k-t-title-lg">{title}</h2>
          <IconButton icon={iconClose} label={closeLabel} onClick={onClose} />
        </div>
        <div className="k-sheet__body">{children}</div>
        {actions === undefined ? null : <div className="k-sheet__actions">{actions}</div>}
      </div>
    </Modal>
  );
}

export interface DialogProps extends Omit<ModalProps, 'className' | 'label'> {
  title: string;
  actions: ReactNode;
  destructive?: boolean;
}

/** A confirmation dialog. Destructive actions always go through one of these. */
export function Dialog({
  open,
  onClose,
  title,
  destructive = false,
  actions,
  children,
}: DialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      label={title}
      className={cx('k-dialog', destructive && 'k-dialog--destructive')}
    >
      <div className="k-dialog__panel">
        <h2 className="k-t-title-lg">{title}</h2>
        <div className="k-t-body-lg k-muted">{children}</div>
        <div className="k-dialog__actions">{actions}</div>
      </div>
    </Modal>
  );
}
